import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Check, X, UserCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { formatDate, formatCurrency } from '../lib/utils';

const REASON_LABELS = {
  tiktok_masked: 'manual_review.buyer_name_reason_masked',
  username_as_name: 'manual_review.buyer_name_reason_username',
};

// Fix 60 (2026-10-16): vacío vale (conserva el de la orden); si hay algo, tienen
// que ser 10 dígitos (o 11 con el 1 adelante) y sin letras. Mismo criterio que
// el backend.
const phoneOk = (p) => {
  const s = String(p || '').trim();
  if (!s) return true;
  if (/[a-z]/i.test(s)) return false;
  let d = s.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  return d.length === 10;
};

export default function BuyerNameReviewCard({ item, itemId, locale }) {
  const { t } = useLang();
  const qc = useQueryClient();
  const [name, setName] = useState(item.tiktok_full_name || '');
  const [notes, setNotes] = useState('');
  // Fix 32 (2026-09-11): la contadora completa también teléfono y dirección
  // con lo que ve en TikTok. Se precargan con lo que haya, salvo que venga
  // enmascarado (asteriscos), en cuyo caso conviene que escriba el dato real.
  const isMasked = (v) => typeof v === 'string' && v.includes('*');
  const initAddr = item.shipping_address || {};
  const clean = (v) => (v && !isMasked(v) ? v : '');
  const [phone, setPhone] = useState(clean(item.phone));
  const [line1, setLine1] = useState(clean(initAddr.line1));
  const [line2, setLine2] = useState(clean(initAddr.line2));
  const [city, setCity] = useState(clean(initAddr.city));
  const [stateV, setStateV] = useState(clean(initAddr.state));
  const [zip, setZip] = useState(clean(initAddr.zip));

  // Fix 38 (2026-09-17): con el switch de autocompletado encendido, la orden
  // SIGUE entrando a Revisión Manual, pero la tarjeta llega con todos los datos
  // del comprador conocido ya cargados, lista para aprobar. La precarga es solo
  // en pantalla: nada se escribe en la orden hasta que la contadora aprieta
  // "Aprobar y crear estimado". La palabra final es siempre de ella.
  const switchQuery = useQuery({
    queryKey: ['auto-fill-known-buyers'],
    queryFn: async () => (await api.get('/admin/auto-fill-known-buyers')).data,
    staleTime: 10000,
  });
  const autoFillOn = !!switchQuery.data?.active;
  const knownMatch = (item.known_buyer || {}).match || null;
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (!autoFillOn || !knownMatch || prefilled) return;
    setName(knownMatch.name || '');
    setPhone(knownMatch.phone || '');
    setLine1(knownMatch.line1 || '');
    setLine2(knownMatch.line2 || '');
    setCity(knownMatch.city || '');
    if (knownMatch.state) setStateV(knownMatch.state);
    if (knownMatch.zip) setZip(knownMatch.zip);
    setPrefilled(true);
  }, [autoFillOn, knownMatch, prefilled]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['manual-review'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
    qc.invalidateQueries({ queryKey: ['orders'] });
  };

  const approveMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/manual-review/${encodeURIComponent(itemId)}/approve-buyer-name`,
        {
          name,
          phone: phone || null,
          address_line1: line1 || null,
          address_line2: line2 || null,
          city: city || null,
          state: stateV || null,
          zip: zip || null,
        })).data,
    onSuccess: (data) => {
      // Fix 33 (2026-09-12): si el auto-sync ya la había creado (TikTok liberó
      // los datos), avisar que NO se creó otra y refrescar.
      if (data?.already_processed) {
        toast.info(t('manual_review.buyer_name_already_processed'));
      } else {
        toast.success(t('manual_review.buyer_name_approve_success'));
      }
      invalidate();
    },
    onError: (err) => {
      const detail = String(err?.response?.data?.detail || '');
      if (detail === 'phone_invalid') { // Fix 60
        toast.error(t('manual_review.buyer_phone_invalid'));
        return;
      }
      if (err?.response?.status === 409 && detail.startsWith('order_locked')) {
        toast.info(t('manual_review.buyer_name_locked'));
        invalidate();
        return;
      }
      toast.error(`${t('manual_review.buyer_name_approve_error')}${detail ? `: ${detail}` : ''}`);
    },
  });

  const discardMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/manual-review/${encodeURIComponent(itemId)}/discard-buyer-name`,
        { notes })).data,
    onSuccess: () => {
      toast.success(t('manual_review.buyer_name_discard_success'));
      invalidate();
    },
    onError: () => toast.error(t('manual_review.buyer_name_discard_error')),
  });

  const addr = item.shipping_address || {};
  const addrLine = [addr.line1, addr.city, addr.state, addr.zip].filter(Boolean).join(', ');
  const isPending = item.status === 'pending';

  return (
    <Card
      className="bg-card border-border border-l-4 border-l-[hsl(var(--destructive))]"
      data-testid={`buyer-name-review-item-${item.tiktok_order_id}`}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-[hsl(var(--destructive))]/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--destructive))]">
            {t('manual_review.type_buyer_name_review')}
          </span>
          {!isPending && (
            <span
              className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              data-testid={`buyer-name-review-status-${item.tiktok_order_id}`}
            >
              {t(`manual_review.status_${item.status}`) || item.status}
            </span>
          )}
          <span className="text-xs text-muted-foreground">{formatDate(item.created_at, locale)}</span>
        </div>

        <div className="grid grid-cols-2 gap-2 text-[13px] sm:grid-cols-4">
          <div>
            <span className="text-muted-foreground">{t('orders.column_order')}: </span>
            <span className="font-mono" data-testid={`buyer-name-review-order-id-${item.tiktok_order_id}`}>
              {item.tiktok_order_id}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">{t('orders.column_auction')}: </span>
            <span className="font-mono">{item.auction_number || '—'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">{t('orders.column_date')}: </span>
            {formatDate(item.order_created_at, locale)}
          </div>
          <div>
            <span className="text-muted-foreground">{t('orders.column_total')}: </span>
            {formatCurrency(item.amount || 0, locale)}
          </div>
        </div>

        <div className="rounded-md bg-muted p-2 text-[13px]">
          <div>
            <span className="text-muted-foreground">{t('manual_review.buyer_name_tiktok_sent')}: </span>
            <span
              className="font-mono font-semibold"
              data-testid={`buyer-name-review-tiktok-name-${item.tiktok_order_id}`}
            >
              {item.tiktok_full_name || '—'}
            </span>
          </div>
          <div className="mt-1 font-medium text-[hsl(var(--destructive))]">
            {t(REASON_LABELS[item.reason] || 'manual_review.buyer_name_reason_masked')}
          </div>
          <div className="mt-1 text-muted-foreground">
            {addrLine || '—'}{item.phone ? ` · ${item.phone}` : ''}
          </div>
        </div>

        {isPending ? (
          <div className="space-y-2">
            {/* Fix 36 (2026-09-17): comprador ya conocido por su cuenta de
                TikTok. Se ofrece con un clic; nunca se aprueba solo desde acá.
                Verde = coincide TODO lo que TikTok dejó visible. Ámbar =
                coincide en parte, hay que revisar los campos marcados. */}
            {(item.known_buyer?.suggestions || []).map((s) => (
              <div
                key={s.source_order_id}
                className={`rounded-md border p-2 text-[12px] ${
                  s.matches
                    ? 'border-emerald-500/40 bg-emerald-500/10'
                    : 'border-amber-500/40 bg-amber-500/10'
                }`}
                data-testid={`known-buyer-suggestion-${item.tiktok_order_id}-${s.source_order_id}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  {s.matches
                    ? <UserCheck className="h-4 w-4 text-emerald-600" />
                    : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                  <span className="font-semibold">
                    {s.matches
                      ? t('manual_review.known_buyer_match')
                      : t('manual_review.known_buyer_partial')}
                  </span>
                  <span className="text-muted-foreground">
                    {t('manual_review.known_buyer_previous')
                      .replace('{{count}}', item.known_buyer?.previous_orders || 0)}
                  </span>
                </div>
                <div className="mt-1">
                  <span className="font-medium">{s.name}</span>
                  {s.phone ? ` · ${s.phone}` : ''}
                </div>
                <div className="text-muted-foreground">
                  {[s.line1, s.line2, s.city, s.state, s.zip].filter(Boolean).join(', ')}
                </div>
                {s.mismatch?.length ? (
                  <div className="mt-1 text-amber-700">
                    {t('manual_review.known_buyer_mismatch')}:{' '}
                    {s.mismatch.map((f) => t(`manual_review.known_buyer_field_${f}`)).join(', ')}
                  </div>
                ) : null}
                <div className="mt-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setName(s.name || '');
                      setPhone(s.phone || '');
                      setLine1(s.line1 || '');
                      setLine2(s.line2 || '');
                      setCity(s.city || '');
                      if (s.state) setStateV(s.state);
                      if (s.zip) setZip(s.zip);
                      toast.success(t('manual_review.known_buyer_used'));
                    }}
                    data-testid={`known-buyer-use-${item.tiktok_order_id}-${s.source_order_id}`}
                  >
                    {t('manual_review.known_buyer_use')}
                  </Button>
                </div>
              </div>
            ))}
            {prefilled && (
              <div
                className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2 text-[12px] font-medium text-emerald-800"
                data-testid={`buyer-prefilled-${item.tiktok_order_id}`}
              >
                {t('manual_review.prefilled_banner')
                  .replace('{{order}}', knownMatch?.source_order_id || '')}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">
              {t('manual_review.buyer_data_help')}
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div>
                <Label className="text-xs text-muted-foreground">
                  {t('manual_review.buyer_name_edit_label')}
                </Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid={`buyer-name-review-input-${item.tiktok_order_id}`}
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">
                  {t('manual_review.buyer_phone_label')}
                </Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  aria-invalid={!phoneOk(phone)}
                  className={phoneOk(phone) ? undefined : 'border-destructive'}
                  data-testid={`buyer-phone-input-${item.tiktok_order_id}`}
                />
                {!phoneOk(phone) && (
                  // Fix 60 (2026-10-16)
                  <p className="mt-1 text-[11px] text-destructive" data-testid={`buyer-phone-invalid-${item.tiktok_order_id}`}>
                    {t('manual_review.buyer_phone_invalid')}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-muted-foreground">
                  {t('manual_review.buyer_address1_label')}
                </Label>
                <Input
                  value={line1}
                  onChange={(e) => setLine1(e.target.value)}
                  data-testid={`buyer-address1-input-${item.tiktok_order_id}`}
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-muted-foreground">
                  {t('manual_review.buyer_address2_label')}
                </Label>
                <Input
                  value={line2}
                  onChange={(e) => setLine2(e.target.value)}
                  data-testid={`buyer-address2-input-${item.tiktok_order_id}`}
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">
                  {t('manual_review.buyer_city_label')}
                </Label>
                <Input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  data-testid={`buyer-city-input-${item.tiktok_order_id}`}
                />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">
                    {t('manual_review.buyer_state_label')}
                  </Label>
                  <Input
                    value={stateV}
                    onChange={(e) => setStateV(e.target.value)}
                    data-testid={`buyer-state-input-${item.tiktok_order_id}`}
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">
                    {t('manual_review.buyer_zip_label')}
                  </Label>
                  <Input
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    data-testid={`buyer-zip-input-${item.tiktok_order_id}`}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <Button
                size="sm"
                disabled={approveMutation.isPending || !name.trim() || !phoneOk(phone)}
                onClick={() => approveMutation.mutate()}
                data-testid={`buyer-name-review-approve-${item.tiktok_order_id}`}
              >
                {approveMutation.isPending
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Check className="h-4 w-4 mr-2" />}
                {t('manual_review.buyer_name_approve_button')}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={discardMutation.isPending}
                onClick={() => discardMutation.mutate()}
                data-testid={`buyer-name-review-discard-${item.tiktok_order_id}`}
              >
                {discardMutation.isPending
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <X className="h-4 w-4 mr-2" />}
                {t('manual_review.buyer_name_discard_button')}
              </Button>
            </div>
          </div>
        ) : (
          item.final_name_used && (
            <div className="text-[12px] text-muted-foreground">
              {t('manual_review.buyer_name_edit_label')}: {' '}
              <span className="font-medium text-foreground">{item.final_name_used}</span>
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
}
